import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import {
  Clapperboard,
  Users,
  CalendarClock,
  MessageSquare,
  PartyPopper,
  User as UserIcon,
  Crown,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { NotificationsBell } from "@/components/NotificationsBell";
import { cn } from "@/lib/utils";
import { useIsDesktop } from "@/hooks/useLowPower";

type DockItem = {
  to: string;
  match: (pathname: string, search: string) => boolean;
  icon: LucideIcon;
  label: string;
  short: string;
  /** gradient stops (HSL via CSS vars) for the active fill */
  gradient: string;
  /** glow color shorthand for active state */
  glow: string;
  badge?: number;
  ariaLabel?: string;
  /** featured "NOW SHOWING" treatment — pulsing gold dot on icon */
  featured?: boolean;
};

interface BulbStripProps {
  count?: number;
  className?: string;
}

const BulbStrip = ({ count = 18, className }: BulbStripProps) => {
  const bulbs = useMemo(() => Array.from({ length: count }), [count]);
  return (
    <div
      aria-hidden
      className={cn("marquee-bulb-strip flex items-center justify-around px-2 pointer-events-none", className)}
    >
      {bulbs.map((_, i) => (
        <span
          key={i}
          className="marquee-bulb"
        />
      ))}
    </div>
  );
};

interface TicketStubProps {
  item: DockItem;
  active: boolean;
  layout: "dock" | "bar";
}

const TicketStub = ({ item, active, layout }: TicketStubProps) => {
  const Icon = item.icon;
  const isBar = layout === "bar";

  return (
    <Link
      to={item.to}
      aria-label={item.ariaLabel ?? item.label}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex shrink-0 flex-col items-center justify-center",
        "transition-transform duration-200 active:scale-95",
        isBar
          ? "min-h-11 min-w-[88px] flex-row gap-2 px-3 py-2 rounded-xl"
          : "h-14 flex-1 min-w-[44px] gap-0.5 rounded-lg"
      )}
    >
      {/* Active fill — soft gradient pad behind icon */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-1 rounded-xl transition-opacity duration-300",
          "bg-gradient-to-br opacity-0 group-hover:opacity-60",
          active && "opacity-100"
        )}
        style={{
          backgroundImage: `linear-gradient(135deg, ${item.gradient})`,
          boxShadow: active ? `0 0 22px ${item.glow}` : undefined,
        }}
      />

      <span
        className={cn(
          "relative z-10 flex items-center justify-center transition-transform duration-200",
          isBar ? "h-5 w-5" : "h-5 w-5",
          "group-hover:-translate-y-0.5",
          active && "drop-shadow-[0_0_6px_hsl(var(--marquee-bulb-glow)/0.7)]"
        )}
      >
        <Icon
          className={cn(
            "h-5 w-5",
            active ? "text-primary-foreground" : "text-foreground/70 group-hover:text-foreground",
            item.featured && !active && "text-amber-300 drop-shadow-[0_0_8px_hsl(45_100%_60%/0.85)]"
          )}
        />
        {item.featured && (
          <span
            aria-hidden
            className="absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_8px_hsl(45_100%_60%/0.95),0_0_14px_hsl(45_100%_60%/0.6)] animate-pulse"
          />
        )}
        {item.badge && item.badge > 0 ? (
          <span
            className={cn(
              "absolute -top-1.5 -right-2 h-4 min-w-4 px-1 rounded-full",
              "bg-destructive text-destructive-foreground text-[10px] font-bold leading-4 text-center",
              "ring-2 ring-background animate-pulse"
            )}
            aria-label={`${item.badge} unread`}
          >
            {item.badge > 9 ? "9+" : item.badge}
          </span>
        ) : null}
      </span>

      <span
        className={cn(
          "relative z-10 font-bold tracking-wide uppercase",
          isBar ? "text-[11px]" : "text-[9px]",
          active
            ? "text-primary-foreground"
            : "text-foreground/65 group-hover:text-foreground"
        )}
      >
        {isBar ? item.label : item.short}
      </span>

      {/* Active underline with projector sweep */}
      {active && (
        <span
          aria-hidden
          className={cn(
            "projector-sweep absolute left-2 right-2 rounded-full",
            isBar ? "-bottom-1 h-[2px]" : "bottom-0.5 h-[2px]",
            "bg-gradient-to-r from-transparent via-marquee-glow to-transparent"
          )}
        />
      )}
    </Link>
  );
};

const SECTION = {
  discover: {
    gradient: "hsl(var(--primary)), hsl(var(--secondary))",
    glow: "hsl(var(--primary) / 0.55)",
  },
  community: {
    gradient: "hsl(340 78% 52%), hsl(22 92% 55%)",
    glow: "hsl(340 78% 52% / 0.5)",
  },
  planner: {
    gradient: "hsl(158 84% 36%), hsl(174 72% 42%)",
    glow: "hsl(160 80% 42% / 0.5)",
  },
  messages: {
    gradient: "hsl(268 78% 58%), hsl(310 76% 55%)",
    glow: "hsl(285 78% 58% / 0.55)",
  },
  night: {
    gradient: "hsl(330 81% 60%), hsl(25 95% 58%), hsl(45 100% 65%)",
    glow: "hsl(25 95% 58% / 0.6)",
  },
  me: {
    gradient: "hsl(45 98% 62%), hsl(25 95% 58%)",
    glow: "hsl(45 98% 62% / 0.55)",
  },
  backstage: {
    gradient: "hsl(210 20% 40%), hsl(45 90% 55%)",
    glow: "hsl(45 90% 55% / 0.5)",
  },
  vip: {
    gradient: "hsl(45 100% 60%), hsl(35 95% 55%), hsl(25 100% 55%)",
    glow: "hsl(45 100% 60% / 0.7)",
  },
} as const;

export const MarqueeDock = () => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const unread = useUnreadMessages();
  const { isAdmin } = useUserRole(user?.id);

  const items: DockItem[] = useMemo(
    () => [
      {
        to: "/",
        match: (p) => p === "/",
        icon: Clapperboard,
        label: "Discover",
        short: "Discover",
        gradient: SECTION.discover.gradient,
        glow: SECTION.discover.glow,
      },
      {
        to: "/community",
        match: (p, s) => p === "/community" && !s.includes("tab=messages") && !s.includes("tab=movie-night"),
        icon: Users,
        label: "Lobby",
        short: "Lobby",
        gradient: SECTION.community.gradient,
        glow: SECTION.community.glow,
      },
      {
        to: "/watch-planner",
        match: (p) => p.startsWith("/watch-planner"),
        icon: CalendarClock,
        label: "Planner",
        short: "Planner",
        gradient: SECTION.planner.gradient,
        glow: SECTION.planner.glow,
        featured: true,
      },
      {
        to: "/community?tab=messages",
        match: (p, s) => (p === "/community" && s.includes("tab=messages")) || p.startsWith("/messages"),
        icon: MessageSquare,
        label: "Messages",
        short: "Msgs",
        gradient: SECTION.messages.gradient,
        glow: SECTION.messages.glow,
        badge: unread,
      },
      {
        to: "/community?tab=movie-night",
        match: (p, s) => (p === "/community" && s.includes("tab=movie-night")) || p.startsWith("/movie-night"),
        icon: PartyPopper,
        label: "Movie Night",
        short: "Night",
        gradient: SECTION.night.gradient,
        glow: SECTION.night.glow,
      },
      {
        to: "/vip",
        match: (p) => p.startsWith("/vip"),
        icon: Crown,
        label: "VIP",
        short: "VIP",
        gradient: SECTION.vip.gradient,
        glow: SECTION.vip.glow,
      },
      ...(isAdmin
        ? [
            {
              to: "/admin",
              match: (p: string) => p.startsWith("/admin"),
              icon: ShieldCheck,
              label: "Backstage",
              short: "Staff",
              gradient: SECTION.backstage.gradient,
              glow: SECTION.backstage.glow,
            } as DockItem,
          ]
        : []),
      {
        to: user ? "/profile" : "/auth",
        match: (p) => p.startsWith("/profile") || p.startsWith("/auth"),
        icon: UserIcon,
        label: user ? "Me" : "Sign in",
        short: user ? "Me" : "In",
        gradient: SECTION.me.gradient,
        glow: SECTION.me.glow,
      },
    ],
    [unread, user, isAdmin]
  );

  const isDesktop = useIsDesktop();

  // Guests navigate the same theatre; only the auth flows hide the dock.
  const HIDDEN_PATHS = ["/auth", "/reset-password"];
  if (HIDDEN_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <>
      {/* MOBILE — fixed bottom dock (primary) */}
      {!isDesktop && <nav
        aria-label="Primary navigation"
        className={cn(
          "lg:hidden fixed bottom-0 inset-x-0 z-40",
          "bg-velvet/95",
          "border-t border-marquee-bulb/30",
          "shadow-[0_-8px_30px_-12px_hsl(var(--marquee-bulb)/0.35)]",
          "safe-bottom"
        )}
      >
        <BulbStrip count={12} className="h-2.5 -mt-px" />
        <ul className="flex items-stretch justify-between gap-1 px-2 pb-1.5 pt-0.5">
          {items.map((item) => (
            <li key={item.to} className="flex-1 min-w-0">
              <TicketStub item={item} active={item.match(pathname, search)} layout="dock" />
            </li>
          ))}
        </ul>
      </nav>}

      {/* DESKTOP — sticky top marquee bar */}
      {isDesktop && <nav
        aria-label="Primary navigation"
        className={cn(
          "hidden lg:block sticky top-0 z-40",
          "bg-velvet/95",
          "border-b border-marquee-bulb/25",
          "shadow-[0_8px_30px_-12px_hsl(var(--marquee-bulb)/0.25)]"
        )}
      >
        <BulbStrip count={36} className="h-2.5" />
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 py-1.5">
            <ul className="flex-1 flex items-center justify-center gap-1">
              {items.map((item) => (
                <li key={item.to}>
                  <TicketStub item={item} active={item.match(pathname, search)} layout="bar" />
                </li>
              ))}
            </ul>
            <NotificationsBell />
          </div>
        </div>
        <BulbStrip count={36} className="h-2.5" />
      </nav>}
    </>
  );
};

export default MarqueeDock;
